import { Outlet, useLocation } from "react-router-dom";
import { SidebarNav } from "@/components/SidebarNav";
import { Topbar } from "@/components/Topbar";
import { PresentationModeBanner } from "@/components/PresentationModeBanner";
import { DriveDeskAIWidget } from "@/components/ai-assistant/DriveDeskAIWidget";
import { TourProvider } from "@/context/TourContext";
import { TourWelcomeDialog } from "@/components/presentation/TourWelcomeDialog";
import { TourSpotlightOverlay } from "@/components/presentation/TourSpotlightOverlay";
import { TourStepPopover } from "@/components/presentation/TourStepPopover";
import { useAuth } from "@/context/AuthContext";
import { isDemoUser } from "@/lib/demoMode";

// Roles with dashboard access today. DriveDesk AI is available to all of
// them (Presentation Mode included, via is_demo scoping in the widget's
// own backend context builder) — kept as an explicit allow-list so a
// future role (e.g. a dedicated Student login) doesn't automatically get
// AI access without a deliberate decision.
const AI_ASSISTANT_ROLES = ["admin", "office_staff", "instructor", "parent"];

const TITLES = {
  "/": "Dashboard",
  "/schedule": "Scheduling",
  "/students": "Students",
  "/instructors": "Instructors",
  "/parents": "Parents",
  "/classrooms": "Classroom Sessions",
  "/vehicles": "Fleet Management",
  "/vehicle-issues": "Vehicle Issues",
  "/forms": "Digital Forms",
  "/payments": "Payments",
  "/reports": "Reports",
  "/expenses": "Expenses",
  "/communication-center": "Announcements",
  "/staff": "Staff & Instructors",
  "/notifications": "Notifications",
  "/time-tracking": "Time Clock",
};

function resolveTitle(pathname) {
  if (TITLES[pathname]) return TITLES[pathname];
  const base = "/" + pathname.split("/")[1];
  return TITLES[base] || "DriveDesk Hub";
}

export const DashboardLayout = () => {
  const location = useLocation();
  const title = resolveTitle(location.pathname);
  const { user } = useAuth();
  const showDemoBanner = isDemoUser(user);

  return (
    <TourProvider>
      <div className="min-h-screen bg-background flex">
        <aside className="hidden lg:flex lg:w-[272px] shrink-0 border-r bg-card fixed inset-y-0 left-0 z-40">
          <SidebarNav />
        </aside>
        <div className="flex-1 lg:ml-[272px] flex flex-col min-h-screen">
          {showDemoBanner && <PresentationModeBanner />}
          <Topbar title={title} />
          <main className="flex-1 px-4 sm:px-6 lg:px-8 py-5 sm:py-6 max-w-[1400px] w-full">
            <Outlet />
          </main>
        </div>
        {AI_ASSISTANT_ROLES.includes(user?.role) && <DriveDeskAIWidget />}
        {showDemoBanner && (
          <>
            <TourWelcomeDialog />
            <TourSpotlightOverlay />
            <TourStepPopover />
          </>
        )}
      </div>
    </TourProvider>
  );
};
